import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { InputComponent } from '../../components/input/input.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import TYPES from '../../constants/types';
import { User } from '../../models/user';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { PsychologistService } from '../../services/psychologist.service';
import { ConfigService } from '../../services/config.service';
import { Psychologist } from '../../models/psychologists';
import { Dialog } from '@angular/cdk/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HeaderComponent } from '../../components/header/header.component';
import { ButtonComponent } from '../../components/button/button.component';

@Component({
  selector: 'app-account',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    TranslatePipe,
    MatIcon,
    MatSelectModule,
    HeaderComponent,
    ButtonComponent
  ],
  templateUrl: './account.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './account.component.scss',
})
export class AccountComponent {

  authService: AuthService = inject(AUTH_SERVICE);
  databaseService: DatabaseService<User> = inject(DATABASE_SERVICE);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  configService: ConfigService = inject(ConfigService);

  psychologist: WritableSignal<Psychologist | null> = signal<Psychologist| null>(null);
  loading: WritableSignal<boolean> = signal<boolean>(false);

  dialog: Dialog = inject(Dialog);
  snackBar:MatSnackBar = inject(MatSnackBar);
  translate: TranslateService = inject(TranslateService);


  firstNameControl: UntypedFormControl;
  lastNameControl: UntypedFormControl;
  birthdateControl: UntypedFormControl;
  dialCodeControl: UntypedFormControl;
  phoneNumberControl: UntypedFormControl;
  searchControl: UntypedFormControl;

  PROFILES:any;
  isEditing:boolean;

  LANGUAGES:any;
  STATUS:any;
  PLANS:any;
  COUNTRY_GROUPS: any;
  COUNTRIES: any;
  BUTTON_TYPES: any

  constructor(){

    this.LANGUAGES = TYPES.LANGUAGES;
    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.STATUS = TYPES.DATABASE.STATUS;
    this.PLANS = TYPES.DATABASE.PLANS;
    this.COUNTRY_GROUPS = TYPES.DATABASE.COUNTRY_GROUPS;
    this.COUNTRIES = TYPES.DATABASE.COUNTRY_GROUPS[1].countries;
    this.BUTTON_TYPES = TYPES.UI.BUTTON;

    this.isEditing = false;
    this.firstNameControl = new UntypedFormControl(
      {
        value: this.authService.user()?.first_name,
        disabled: !this.isEditing
      },
      [Validators.required]);
    this.lastNameControl = new UntypedFormControl(
      {
        value: this.authService.user()?.last_name,
        disabled: !this.isEditing
      },
      [Validators.required]);
    this.birthdateControl = new UntypedFormControl(
      {
        value: this.authService.user()?.birthdate,
        disabled: !this.isEditing
      },
      [Validators.required]);
    
    const phoneNumber = this.extractAndFormatPrefix(this.authService.user()?.phone_number, this.getFullCleanCodes(this.COUNTRIES));  

    this.dialCodeControl = new UntypedFormControl(
      {
        value: phoneNumber.dialCode,
        disabled: !this.isEditing
      },
      []
    );
    this.phoneNumberControl = new UntypedFormControl(
      {
        value: phoneNumber.phoneNumber,
        disabled: !this.isEditing
      },
      []
    );  

    const language = this.LANGUAGES.find((item:any)=>{return item.locale === this.configService.preferedLanguage()});

    this.searchControl = new UntypedFormControl(
      {
        value: language ,
        disabled: !this.isEditing
      },
      [Validators.required, this.objectValidator()]
    );

    this.searchControl.valueChanges.subscribe(
    (value) => {
      if(typeof value === 'object'){
        this.configService.setLanguage(value.locale);
      };
    });
  }

  back(){
    history.back();
  }

  getUserProfile(){
    switch(this.authService.user()?.profile){
      case 1: 
        return this.PROFILES['USER']
      case 2:
        return this.PROFILES['PSYCHOLOGIST']
      case 3:
        return this.PROFILES['ADMIN']
      default:
        return this.PROFILES['USER']
    }
  }

  async editToggle(){

    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    if(this.isEditing
      && (this.firstNameControl.invalid
      || this.lastNameControl.invalid
      || this.birthdateControl.invalid
      || this.dialCodeControl.invalid
      || this.phoneNumberControl.invalid
      || this.searchControl.invalid)
    ){
      const message:string = this.translate.instant('info.invalid_form');
      const action:string = this.translate.instant('buttons.dismiss');
      this.snackBar.open(message, action);
      return;
    } 

    if(this.isEditing 
      && (this.firstNameControl.dirty
      || this.lastNameControl.dirty
      || this.birthdateControl.dirty
      || this.dialCodeControl.dirty
      || this.phoneNumberControl.dirty
      || this.searchControl.dirty)
    ){
      const user: Partial<User> | null = this.authService.user();
      if(user){
        user.first_name = this.firstNameControl.value;
        user.last_name = this.lastNameControl.value;
        if(this.birthdateControl.value instanceof Date){
          user.birthdate = this.toPostgresDateString(this.birthdateControl.value);
        }else{
          user.birthdate = this.birthdateControl.value;
        }
        user.phone_number = (this.dialCodeControl.value + this.phoneNumberControl.value).replace(/\D/g, '');
        user.prefered_language = this.searchControl.value.locale;

        const id = user.id;
        if(id){
          try{
            await this.databaseService.update(TYPES.DATABASE.TABLES.USER, id, user);
            await this.authService.updateUser(user);
            if(this.authService.user()?.profile === this.PROFILES.PSYCHOLOGIST.key){
              const psychologistId = this.psychologist()?.id
              if(psychologistId){
                this.psychologistService.update(
                  TYPES.DATABASE.TABLES.PSYCHOLOGISTS,
                  psychologistId,
                  {
                    first_name: user.first_name,
                    last_name: user.last_name,
                    phone_number: user.phone_number
                  }
                );
              }
            }
            const message:string = this.translate.instant('info.user_updated');
            const action:string = this.translate.instant('buttons.dismiss');
            this.snackBar.open(message, action);
          }catch(error:any){
            let message = ''; 
            if(error.toString().includes('users_phone_number_key')){
              message = this.translate.instant('info.phone_number_duplicatad');              
            }else{
              message = this.translate.instant('info.unkonwn_error', { error: error.toString() } );
            }
            const action:string = this.translate.instant('buttons.dismiss');
            this.snackBar.open(message, action);
          }
        }
      }
    }
    this.isEditing = !this.isEditing;
    if(this.isEditing){
      this.firstNameControl.enable();
      this.lastNameControl.enable();
      this.birthdateControl.enable();
      this.dialCodeControl.enable();
      this.phoneNumberControl.enable();
      this.searchControl.enable();
    }else{
      this.firstNameControl.disable();
      this.lastNameControl.disable();
      this.birthdateControl.disable();
      this.dialCodeControl.disable();
      this.phoneNumberControl.disable();
      this.searchControl.disable();
    }
  }

  private toPostgresDateString(date: Date | null): string | null {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; // YYYY-MM-DD
  }

  autocompleteDisplayFn(item:any): string {
    return item.name;
  }

  objectValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (value === null || value === undefined) {
        return null;
      }

      if (typeof value !== 'object') {
        return { notObject: true };
      }

      return null;
    };
  }

  /**
   * Extrai o DDI de um número completo e adiciona o "+"
   * @param fullNumber O número completo (ex: "5511999999999")
   * @param knownCodes Lista de códigos de país (ex: ["55", "1", "351", "44"])
   */
  extractAndFormatPrefix(fullNumber: string | null | undefined, knownCodes: string[]): {dialCode: string, phoneNumber: string} {
    if (!fullNumber) return {dialCode: '', phoneNumber: ''};

    // 1. Remove qualquer caractere não numérico para garantir a comparação
    const cleanNumber = fullNumber.replace(/\D/g, '');

    // 2. Ordena os códigos por tamanho (decrescente).
    // Isso é crucial! Se tivermos EUA (1) e Bahamas (1242),
    // precisamos checar o mais longo primeiro para não dar "falso positivo" no 1.
    const sortedCodes = [...knownCodes].sort((a, b) => b.length - a.length);

    // 3. Procura qual código coincide com o início do número
    const foundCode = sortedCodes.find(code => cleanNumber.startsWith(code));

    if (foundCode) {
      return {
        dialCode: `+${foundCode}`,
        phoneNumber: cleanNumber.replace(foundCode, '')
      };
    }

    // Fallback: Se não achar na lista, retorna vazio ou o "+" genérico
    return {dialCode: '', phoneNumber: ''};
  }

  /**
   * Filtra a lista de países para retornar o DDI + Código de Área/Região concatenados.
   * Ex: De "+1-246", retorna "1246". De "+355", retorna "355".
   * @param countries A lista completa de países.
   * @returns Um array de strings limpas e únicas (ex: ["1246", "355", "93"]).
  */
  getFullCleanCodes(countries: any[]): string[] {
    
    const cleanCodes = countries.map(country => {
        // Usa regex para remover TODOS os caracteres que NÃO são dígitos (+, -, espaço, etc.)
        return country.dial_code.replace(/\D/g, ''); 
    });

    // Remove duplicatas (ex: se o país "EUA" e "Canadá" tiverem o dial_code apenas "+1")
    return Array.from(new Set(cleanCodes)).filter(code => code.length > 0);
  }

  sortCountriesByTranslatedName(countries: any[]){
    return countries.sort(
      (a, b) => {
        return this.translate.instant(a.name).localeCompare(this.translate.instant(b.name))
      }
    )
  }

  isFormInvalid(){
    if(this.firstNameControl.invalid
      || this.lastNameControl.invalid
      || this.birthdateControl.invalid
      || this.dialCodeControl.invalid
      || this.phoneNumberControl.invalid
      || this.searchControl.invalid)
      return true;
    return false;
  }

}
